import {
    useQuery,
    useMutation,
    UseQueryOptions,
    UseQueryResult,
    UseMutationOptions,
    UseMutationResult,
    useQueryClient
} from '@tanstack/react-query';
import {
    InAppNotificationDto,
    NotificationPreferenceDto,
    UnreadCountDto
} from '../../types/notificationTypes';
import {useCreateAuthenticatedFetch} from '../useCreateAuthenticatedFetch';
import baseUrl from '../baseUrl';
import {handleResponse} from '../handleResponse';

const NOTIFICATIONS_QUERY_KEY = ['notifications'];
const UNREAD_COUNT_QUERY_KEY = ['notifications', 'unread-count'];
const PREFERENCES_QUERY_KEY = ['notifications', 'preferences'];

export const useGetNotifications = (
    take = 50,
    options?: UseQueryOptions<InAppNotificationDto[], Error>
): UseQueryResult<InAppNotificationDto[], Error> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();

    return useQuery({
        queryKey: [...NOTIFICATIONS_QUERY_KEY, take],
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/notifications?take=${take}`
            );
            return handleResponse<InAppNotificationDto[]>(response);
        },
        ...options
    });
};

export const useGetUnreadCount = (
    options?: UseQueryOptions<UnreadCountDto, Error>
): UseQueryResult<UnreadCountDto, Error> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();

    return useQuery({
        queryKey: UNREAD_COUNT_QUERY_KEY,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/notifications/unread-count`
            );
            return handleResponse<UnreadCountDto>(response);
        },
        refetchInterval: 60_000, // Poll every 60 seconds
        ...options
    });
};

export const useMarkNotificationRead = (
    options?: UseMutationOptions<boolean, Error, string>
): UseMutationResult<boolean, Error, string> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/notifications/${id}/mark-read`,
                {method: 'POST'}
            );
            return handleResponse<boolean>(response);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: NOTIFICATIONS_QUERY_KEY});
            queryClient.invalidateQueries({queryKey: UNREAD_COUNT_QUERY_KEY});
        },
        ...options
    });
};

export const useMarkAllNotificationsRead = (
    options?: UseMutationOptions<boolean, Error, void>
): UseMutationResult<boolean, Error, void> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/notifications/mark-all-read`,
                {method: 'POST'}
            );
            return handleResponse<boolean>(response);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: NOTIFICATIONS_QUERY_KEY});
            queryClient.invalidateQueries({queryKey: UNREAD_COUNT_QUERY_KEY});
        },
        ...options
    });
};

export const useGetNotificationPreferences = (
    options?: UseQueryOptions<NotificationPreferenceDto, Error>
): UseQueryResult<NotificationPreferenceDto, Error> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();

    return useQuery({
        queryKey: PREFERENCES_QUERY_KEY,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/notifications/preferences`
            );
            return handleResponse<NotificationPreferenceDto>(response);
        },
        ...options
    });
};

export const useUpdateNotificationPreferences = (
    options?: UseMutationOptions<
        NotificationPreferenceDto,
        Error,
        NotificationPreferenceDto
    >
): UseMutationResult<
    NotificationPreferenceDto,
    Error,
    NotificationPreferenceDto
> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (prefs: NotificationPreferenceDto) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/notifications/preferences`,
                {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(prefs)
                }
            );
            return handleResponse<NotificationPreferenceDto>(response);
        },
        onSuccess: data => {
            queryClient.setQueryData(PREFERENCES_QUERY_KEY, data);
        },
        ...options
    });
};
